import { Component, ContentChild, ElementRef, EventEmitter, forwardRef, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { Observable, Subscription, timer } from 'rxjs';
import { take } from 'rxjs/operators';

import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";

import { faSpinner, IconDefinition } from "@fortawesome/free-solid-svg-icons";

interface IAutocompleteInputOption {
    id?: number;
}

@Component({
    selector: 'autocomplete-input',
    templateUrl: './autocomplete-input.component.html',
    styleUrls: ["./autocomplete-input.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteInputComponent),
            multi: true,
        }
    ]
})
export class AutocompleteInputComponent<TValue> implements ControlValueAccessor {
    @Input()
    public placeholder: string;
    @Input()
    public forceFocus: boolean;
    @Input()
    public forceBlur: boolean;
    @Input()
    public delayBlur: boolean = true;
    @Input()
    public displayFn: (data: TValue) => string;
    @Input()
    public isExactMatchFn: (data: TValue, text: string) => boolean;
    @Input()
    public createFn: (text: string) => TValue;
    @Input()
    public options: Observable<TValue[]>;
    @Input()
    public isLoading: boolean;
    @Input()
    public allowSelectOnBlur: boolean = false;
    @Input()
    public icon: string;
    @Input()
    public iconDefinition: string;
    @Input()
    public autoActiveFirstOption: boolean = true;
    @Input()
    public autocompleteDisabled: boolean = false;
    @Input()
    public autocompleteTransparent: boolean = false;
    @Input()
    public formControl: FormControl;
    @Input()
    public removeEmptyOptions: boolean= false;

    @Output()
    public readonly onChangeInput: EventEmitter<string> = new EventEmitter();
    @Output()
    public readonly onBlur: EventEmitter<FocusEvent> = new EventEmitter();
    @Output()
    public readonly onInputBlur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output()
    public readonly onFocus: EventEmitter<FocusEvent> = new EventEmitter();
    @Output()
    public readonly onValueSelected: EventEmitter<TValue> = new EventEmitter();
    @Output()
    public readonly onOpenPanel: EventEmitter<void> = new EventEmitter();
    @Output()
    public readonly onClosePanel: EventEmitter<void> = new EventEmitter();
    @Output()
    public readonly onKeyUp: EventEmitter<KeyboardEvent> = new EventEmitter();

    @ContentChild(TemplateRef)
    public template: TemplateRef<ElementRef>;

    @ViewChild('input')
    public input: ElementRef;
    @ViewChild('matAutocomplete')
    public matAutocomplete: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger)
    public autocomplete: MatAutocompleteTrigger;

    public value: TValue;

    public iconSpinner: IconDefinition = faSpinner;

    private onChange: (value: TValue) => void;
    private onTouched?: () => void;

    private blurHandle: Subscription;
    private focusHandle: Subscription;

    private previousValue: TValue;

    constructor(
        private readonly errorTracingService: ErrorTracingService,
    ) {}

    public optionSelected(event: MatAutocompleteSelectedEvent): void {
        this.blurHandle?.unsubscribe();
        this.blurHandle = undefined;

        this.writeValue(event.option.value);
        this.onChangeInput.emit(this.displayFn ? this.displayFn(event.option.value) : event.option.value);
        this.triggerTouched();

        timer(1).subscribe((): void => {
            this.onValueSelected.emit(event.option.value);
            this.input?.nativeElement.focus();

            this.focusHandle?.unsubscribe();
            this.focusHandle = undefined;
        });
    }

    public keyUp(event: KeyboardEvent): void {
        if (event.key === 'Escape' || event.key === 'Esc') {
            this.writeValue(this.previousValue);
        }

        this.onKeyUp.emit(event);
    }

    public onBlurEvent(event: FocusEvent): void {
        if(this.delayBlur){
            this.blurHandle?.unsubscribe();
            // We don't want to do the blur processing if the user chose an element in the list
            this.blurHandle = timer(100).subscribe((): void => {
                this.afterBlur(event);
            });
        }else {
            this.onInputBlur.emit(event);
        }
    }

    public onFocusEvent(event: FocusEvent): void {
        this.focusHandle?.unsubscribe();

        this.focusHandle = timer(1).subscribe((): void => {
            this.afterFocus(event);
        });

        this.input.nativeElement.select();
    }

    public inputChanged(event: UIEvent): void {
        this.initValue(null, false);
        const value: string = (event.target as HTMLInputElement).value;
        this.onChangeInput.emit(value);
    }

    /**
     * By default, track by a property named "id" if present
     * otherwise, use the object itself
     */
    // tslint:disable-next-line:no-any
    public getOptionTrackBy(option: TValue): any {
        return option && (option as IAutocompleteInputOption).id ? (option as IAutocompleteInputOption).id : option;
    }

    public openPanel(): void {
        this.autocomplete.setDisabledState(false);
        this.autocomplete.autocompleteDisabled = false;
        this.autocomplete.openPanel();
    }

    public closePanel(): void {
        this.autocomplete.openPanel();
    }

    // Interface ControlValueAccessor
    public writeValue(value: TValue): void {
        this.previousValue = value;
        this.setInputValue(value);
        this.initValue(value, true);
    }

    // Allows Angular to register a function to call when the model (rating) changes.
    // Save the function as a property to call later here.
    public registerOnChange(fn: (values: TValue) => void): void {
        this.onChange = fn;
    }

    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public focus(): void {
        if(this.input){
            this.input.nativeElement.focus();
        }
    }

    public blur(): void {
        if(this.input){
            this.input.nativeElement.blur();
        }
    }

    private initValue(value: TValue, forceChange: boolean): void {
        const previousValue: TValue = this.value;

        this.value = value;

        if (value || forceChange) {
            this.setInputValue(value);
        }

        if (this.onChange && (forceChange || previousValue !== value)) {
            this.onChange(value);
            this.triggerTouched();
        }
    }

    private setInputValue(value: TValue | string): void {
        if(this.input){
            this.input.nativeElement.value = this.displayFn && typeof value !== "string" ? this.displayFn(value) : value;
        }
    }

    private triggerTouched(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    private afterBlur(event: FocusEvent): void {
        this.blurHandle = null;
        const searched: string = this.input ? this.input.nativeElement.value : null;

        if (searched && this.options && !this.value) {
            this.options.pipe(
                take(1),
            ).subscribe({
                next: (values: TValue[]): void => {
                    if (searched && values && values.length && this.isExactMatchFn && this.isExactMatchFn(values[0], searched)) {
                        this.writeValue(values[0]);
                    } else if (this.createFn && searched) {
                        this.writeValue(this.createFn(searched));
                    } else {
                        this.writeValue(null);
                    }

                    this.triggerTouched();
                    this.autocomplete.closePanel();
                    this.onBlur.emit(event);
                },
                error: (error: Error): void => {
                    this.errorTracingService.captureException(error);
                    this.writeValue(null);
                    this.autocomplete.closePanel();
                    this.onBlur.emit(event);
                },
            });
        } else if (!this.value) {
            this.writeValue(null);
            this.triggerTouched();
            this.autocomplete.closePanel();
            this.onBlur.emit(event);
        } else {
            this.triggerTouched();
            this.autocomplete.closePanel();
            this.onBlur.emit(event);

        }
    }

    private afterFocus(event: FocusEvent): void {
        this.onFocus.emit(event);
        this.focusHandle = undefined;
    }
}
